// MUI アイコン（CloseIcon）をインポート
import CloseIcon from '@mui/icons-material/Close';
// MUI レイアウトコンポーネントをインポート
import { Box, Button, Dialog, DialogActions, DialogContent, IconButton, Modal, Typography } from '@mui/material';
// AG-Grid スタイル（基本）をインポート
import 'ag-grid-community/styles/ag-grid.css';
// AG-Grid スタイル（Alpine テーマ）をインポート
import 'ag-grid-community/styles/ag-theme-alpine.css';
// AG-Grid React コンポーネントをインポート
import { AgGridReact } from 'ag-grid-react';
// notistack からスナックバーフックをインポート
import { useSnackbar } from 'notistack5';
// React とフックをインポート
import React, { useEffect, useRef, useState } from 'react';
// react-i18next から翻訳フックをインポート
import { useTranslation } from 'react-i18next';
// スタイル付きボタンコンポーネントをインポート
import { StyledButton } from 'src/components/app-components/StyledButton';
// Redux セレクターフックをインポート
import { useAppSelector } from 'src/redux/store';
// アンケート回答グリッドフックをインポート
import { useEnqueteAnswerGrid } from '../../../hooks/useEnqueteAnswerGrid';
// ファイル型定義をインポート
import { FileType } from '../../../store/enqueteAnswerSlice';
// グリッドオプションをインポートします（コメント）
// グリッドオプションをインポートします

// luxon から日付フォーマット関数をインポート
import { DateTime } from 'luxon';
// 定数（日付フォーマット、アップロードファイル情報）をインポート
import { DATE_TIME_FORMAT_STRING, UPLOAD_FILE_INFO } from 'src/constants';
// 定数（ANSWER_STATUS）をインポート
import { ANSWER_STATUS } from 'src/constants';
import { usePermission } from 'src/hooks/usePermission';
// バイト数をサイズ文字列に変換するユーティリティをインポート
import bytesToSize from 'src/utils/bytesToSize';
import { v4 as uuidv4 } from 'uuid';

// モーダルのプロパティ型定義インターフェース
interface SelectFileModalProps {
  // モーダルの開閉状態
  open: boolean;
  // モーダルを閉じるためのコールバック関数
  onClose: () => void;
  // 親コンポーネントにファイルを渡すためのコールバック関数
  onFileSelected: (file: FileType[]) => void;
  // ファイル削除時のコールバック関数
  onFileDeleted: (file: FileType[]) => void;
}
// ファイル選択モーダルコンポーネントの定義
const SelectFileModal: React.FC<SelectFileModalProps> = ({ open, onClose, onFileSelected, onFileDeleted }) => {
  // スナックバー表示フックを使用
  const { enqueueSnackbar } = useSnackbar();
  // 翻訳関数を取得
  const { t } = useTranslation();
  // ステートの定義（エラーメッセージ）
  const [error] = useState<string | null>(null);
  // AG-Grid API の参照を定義
  const gridApiRef = useRef<any>(null);
  // Redux からアップロードファイルデータを取得
  const uploadFileData = useAppSelector((state) => state.enqueteAnswer.uploadFileData);
  // Redux から回答者ターゲットデータを取得
  const respondentTargetData = useAppSelector((state) => state.enqueteAnswer.respondentTargetData);
  // ファイルダウンロードフックを使用
  const { downloadFile } = useEnqueteAnswerGrid();
  // 確認ダイアログオープン状態のステートを定義
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  // ボタンの活性フラグのステートを定義
  const [isDisabled, setIsDisabled] = useState(true);
  // 選択されたファイルのリストのステートを定義
  const [selectedFiles, setSelectedFiles] = useState<FileType[]>([]);
  // 以前のファイルリストのステートを定義
  const [preFiles, setPreFiles] = useState<FileType[]>([]);
  // 権限フックを使用（各種ロールフラグを取得）
  const { isAdmin, isSurveyOwner, isRespondentPerson, isAnswerEditor, isConfirmor } = usePermission();
  const enqueteAnswerData = useAppSelector((state) => state.enqueteAnswer.enqueteAnswerData);
  const isEnqueteAnswerOpen = useAppSelector((state) => state.enqueteAnswer.isEnqueteAnswerOpen);
  const canEdit =
    isAdmin || isSurveyOwner || isRespondentPerson || isAnswerEditor || (isConfirmor && enqueteAnswerData.status === ANSWER_STATUS.ANSWERED);

  // // モーダルオープン時にファイルリストを初期化する効果フック
  // useEffect(() => {
  //   // モーダルが開いている場合
  //   if (open) {
  //     // ボタンを無効化
  //     setIsDisabled(true);
  //     // 新しい選択ファイル変数を宣言
  //     let newSelectedFiles;
  //     // 以前のファイルリストをマッピングしてコピー
  //     newSelectedFiles = preFiles.map((element) => ({
  //       // ファイル名を設定
  //       fileName: element.fileName,
  //       // サイズを設定
  //       size: element.size,
  //       // オブジェクトキー名を設定
  //       objectKeyName: element.objectKeyName,
  //       // S3 パスを設定
  //       s3Path: element.s3Path,
  //       // 作成日時を設定
  //       createdAt: element.createdAt,
  //       // 更新日時を設定
  //       updatedAt: element.updatedAt,
  //       // 作成者を設定
  //       createdBy: element.createdBy,
  //       // 更新者を設定
  //       updatedBy: element.updatedBy,
  //       // 新規フラグを設定
  //       isNew: element.isNew,
  //     }));
  //     // 選択されたファイル状態を更新
  //     setSelectedFiles(newSelectedFiles);
  //     // 親コンポーネントにファイルを選択済みとして通知
  //     onFileSelected(selectedFiles);
  //   }
  // }, [open]);

  // アップロードファイルデータ変更時にファイルリストを更新する効果フック
  useEffect(() => {
    // 新しい選択ファイル変数を宣言
    let newSelectedFiles;
    // アップロードファイルデータをマッピングしてコピー
    newSelectedFiles = uploadFileData.map((element) => ({
      // ファイル名を設定
      fileName: element.fileName,
      // サイズを設定
      size: element.size,
      // オブジェクトキー名を設定
      objectKeyName: element.objectKeyName,
      // S3 パスを設定
      s3Path: element.s3Path,
      // 作成日時を設定
      createdAt: element.createdAt,
      // 更新日時を設定
      updatedAt: element.updatedAt,
      // 作成者を設定
      createdBy: element.createdBy,
      // 更新者を設定
      updatedBy: element.updatedBy,
    }));
    // 選択されたファイル状態を更新
    setSelectedFiles(newSelectedFiles);
    // 以前のファイルリスト状態を更新
    setPreFiles(newSelectedFiles);
    // 親コンポーネントにファイルを選択済みとして通知
    onFileSelected(newSelectedFiles);
  }, [uploadFileData]);

  // ファイル選択の処理ハンドラー
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // ファイルが選択された場合
    if (event.target.files) {
      // 選択した複数のファイルを配列に変換
      const newFiles = Array.from(event.target.files);
      // 既存のファイル名リストを取得
      const existingFileNames = selectedFiles.map((file) => file.fileName);

      // 更新されたファイルリストを生成
      const updatedFiles: FileType[] = newFiles.map((file) => {
        // 最後のドットのインデックスを取得
        let lastDotIndex = file.name.lastIndexOf('.');
        // ベース名と拡張子変数を宣言
        let baseNameOnly: string, baseExtension: string;
        // ドットがない場合
        if (lastDotIndex === -1) {
          // ベース名のみを設定
          baseNameOnly = file.name;
          // 拡張子を空文字に設定
          baseExtension = '';
        } else {
          // ベース名を設定
          baseNameOnly = file.name.substring(0, lastDotIndex);
          // 拡張子を設定
          baseExtension = file.name.substring(lastDotIndex + 1);
        }
        // ファイル名変数を宣言
        let fileName = file.name;
        // カウント変数を宣言
        let count = 1;
        // 既存のファイル名に重複がある場合
        while (existingFileNames.includes(fileName)) {
          // ファイル名にカウントを追加して重複回避
          fileName = `${baseNameOnly} (${count})${'.' + baseExtension}`;
          // カウントをインクリメント
          count++;
        }
        const key = uuidv4().toString();
        // 新しいファイルオブジェクトを返す
        return { isNew: true, fileName: fileName, size: file.size, objectKeyName: key, file: new File([file], fileName, { type: file.type }) };
      });
      // input の値をリセット
      event.target.value = '';
      // 選択されたファイルリストに新しいファイルを追加
      setSelectedFiles([...selectedFiles, ...updatedFiles]);
    }
  };

  // ファイルアップロードの処理ハンドラー
  const handleFileUpload = async (): Promise<boolean> => {
    // サイズエラーフラグ
    let sizeFlg = false;
    // 拡張子エラーフラグ
    let extensFlg = false;
    // 長さエラーフラグ
    let lengthFlg = false;
    // ファイル数エラーフラグ
    let fileCountflg = false;

    // 選択されたファイルごとに検証ループ
    for (const file of selectedFiles) {
      // ファイルサイズチェック
      if (file.size > UPLOAD_FILE_INFO.NAX_FILE_SIZE) {
        // サイズエラーフラグを立てる
        sizeFlg = true;
      }

      // ファイル拡張子チェック
      // 拡張子を取得
      const extension = file.fileName.split('.').pop()?.toLowerCase();
      // 許可された拡張子リストに含まれていない場合
      if (!UPLOAD_FILE_INFO.ALLOWED_EXTENSIONS.includes(extension || '')) {
        // 拡張子エラーフラグを立てる
        extensFlg = true;
      }

      // ファイル名長さチェック
      // 最大長を超えている場合
      if (file.fileName.length > UPLOAD_FILE_INFO.MAX_FILE_NAME_LENGTH) {
        // 長さエラーフラグを立てる
        lengthFlg = true;
      }
    }
    // ファイル数チェック
    if (selectedFiles.length > UPLOAD_FILE_INFO.MAX_FILE_COUNT) {
      // ファイル数エラーフラグを立てる
      fileCountflg = true;
    }
    // サイズエラーの場合スナックバー表示
    if (sizeFlg) {
      enqueueSnackbar(t('enqueteAnswerInput.modal.fileAttachment.message.fileSizeCheck'), {
        // エラーバリアント
        variant: 'error',
      });
    }
    // 拡張子エラーの場合スナックバー表示
    if (extensFlg) {
      enqueueSnackbar(t('enqueteAnswerInput.modal.fileAttachment.message.fileSuffixCheck'), {
        // エラーバリアント
        variant: 'error',
      });
    }
    // 長さエラーの場合スナックバー表示
    if (lengthFlg) {
      enqueueSnackbar(t('enqueteAnswerInput.modal.fileAttachment.message.fileNameLengthCheck'), {
        // エラーバリアント
        variant: 'error',
      });
    }

    // ファイル数エラーの場合スナックバー表示
    if (fileCountflg) {
      enqueueSnackbar(t('enqueteAnswerInput.modal.fileAttachment.message.numberOfFilesCheck'), {
        // エラーバリアント
        variant: 'error',
      });
    }
    // エラーフラグのいずれかが true なら true を返す
    return fileCountflg || lengthFlg || extensFlg || sizeFlg;
  };

  // 選択された行を削除する処理ハンドラー
  const handleDeleteSelectedRows = () => {
    // Grid API が存在する場合
    if (gridApiRef.current) {
      // 選択されたノードを取得
      const selectedNodes = gridApiRef.current.getSelectedNodes();
      // 選択されたデータを取得
      const selectedData = selectedNodes.map((node: any) => node.data);
      // 選択されたファイル名のリストを取得
      const selectedFileNames = selectedData.map((data: { name: string }) => data.name);
      // 選択されたデータを除外したファイルリストを生成
      const updatedFiles = selectedFiles.filter((file) => !selectedFileNames.includes(file.fileName));
      // 更新されたファイルリストを設定
      setSelectedFiles(updatedFiles);
      // すべての選択を解除
      gridApiRef.current.deselectAll();
    }
  };

  // 選択変更時のイベントハンドラー
  const onSelectionChanged = (event: any) => {
    // 選択されたノードを取得
    const selectedNodes = event.api.getSelectedNodes();
    if (!canEdit || !isEnqueteAnswerOpen) {
      setIsDisabled(true);
    } else {
      // 選択がない場合はボタンを無効化
      setIsDisabled(selectedNodes.length <= 0);
    }
  };

  // 更新があるか判定する関数
  const onUpdate = () => {
    // 配列が等しいか返す
    return arraysEqual(preFiles, selectedFiles);
  };

  // キャンセル処理関数
  const cancel = () => {
    // 配列が等しい場合
    if (arraysEqual(preFiles, selectedFiles)) {
      // モーダルを閉じる
      onClose();
    } else {
      // 確認ダイアログを開く
      setConfirmOpen(true);
    }
  };
  // キャンセル確認処理ハンドラー
  const handleCancelConfirm = () => {
    // 確認ダイアログを閉じる
    setConfirmOpen(false);
  };

  // いいえ確認処理ハンドラー
  const handleNoconfirm = () => {
    // 選択されたファイルを以前のファイルにリセット
    setSelectedFiles(
      preFiles.map((element) => ({
        // ファイル名を設定
        fileName: element.fileName,
        // サイズを設定
        size: element.size,
        // オブジェクトキー名を設定
        objectKeyName: element.objectKeyName,
        // S3 パスを設定
        s3Path: element.s3Path,
        // 作成日時を設定
        createdAt: element.createdAt,
        // 更新日時を設定
        updatedAt: element.updatedAt,
        // 作成者を設定
        createdBy: element.createdBy,
        // 更新者を設定
        updatedBy: element.updatedBy,
      })),
    );
    // 確認ダイアログを閉じる
    setConfirmOpen(false);
    // モーダルを閉じる
    onClose();
  };

  // 確認処理ハンドラー
  const handleConfirm = async () => {
    // ファイルアップロード検証が成功した場合
    if (!!!(await handleFileUpload())) {
      // 以前のファイルリストを更新
      setPreFiles(selectedFiles);
      // 選択されたファイルのキーセットを生成
      const dataKeys = new Set(selectedFiles.map((item) => item.objectKeyName));
      // 削除されたファイル（アップロードデータに含まれていないもの）をフィルタ
      const difference = uploadFileData.filter((item) => item.objectKeyName !== undefined && !dataKeys.has(item.objectKeyName));
      // 親コンポーネントにファイル削除を通知
      onFileDeleted(difference);
      // 親コンポーネントにファイル選択を通知
      onFileSelected(selectedFiles);
      // モーダルを閉じる
      onClose();
    }
    // 確認ダイアログが開いている場合
    if (confirmOpen) {
      // 確認ダイアログを閉じる
      setConfirmOpen(false);
    }
  };
  // ファイルをダウンロードする処理ハンドラー
  const handleDownloadFile = (file: any) => {
    // ファイルオブジェクトが存在する場合
    if (file.file) {
      // オブジェクト URL を作成
      const url = URL.createObjectURL(file.file);
      // 匿名リンク要素を作成
      const a = document.createElement('a');
      // href に URL を設定
      a.href = url;
      // download 属性にファイル名を設定
      a.download = file.name;
      // body に追加
      document.body.appendChild(a);
      // クリックイベントを発火
      a.click();
      // body から削除
      document.body.removeChild(a);
      // URL を解放
      URL.revokeObjectURL(url);
    } else if (file.objectKeyName) {
      // ファイル名を取得
      const fileName = file.name;
      // 最後のドットのインデックスを取得
      const lastDotIndex = fileName.lastIndexOf('.');
      // ベース名と拡張子変数を宣言
      let fileBaseName: string, extent: string;
      // ドットがない場合
      if (lastDotIndex === -1) {
        // ベース名を設定
        fileBaseName = fileName;
        // 拡張子を空文字に設定
        extent = '';
      } else {
        // ベース名を設定
        fileBaseName = fileName.substring(0, lastDotIndex);
        // 拡張子を設定
        extent = fileName.substring(lastDotIndex + 1);
      }
      // 作成時間変数を宣言
      let createTime;
      try {
        // 日付をフォーマット
        const dt = DateTime.fromISO(file.createdAt, { zone: DATE_TIME_FORMAT_STRING.TIME_ZONE_TOKYO });
        createTime = dt.toFormat(DATE_TIME_FORMAT_STRING.YMD_HMSS);
      } catch (error) {
        // エラー場合はそのまま使用
        createTime = file.createdAt;
      }

      // ファイルダウンロード関数を呼び出し
      downloadFile(
        // オブジェクトキー名
        file.objectKeyName,
        // S3 パス
        file.s3Path ? file.s3Path : '',
        // 生成されたファイル名
        `${
          respondentTargetData.organization?.companyAbbreviation?.trim()
            ? respondentTargetData.organization?.companyAbbreviation
            : respondentTargetData.organization?.companyNameJpn.length &&
              respondentTargetData.organization?.companyNameJpn.length > UPLOAD_FILE_INFO.MAX_CORP_NAME
            ? respondentTargetData.organization?.companyNameJpn.substring(0, UPLOAD_FILE_INFO.MAX_CORP_NAME)
            : respondentTargetData.organization?.companyNameJpn
        }_${
          respondentTargetData.organization?.divisionCode.startsWith('TEMPORARY')
            ? '仮-' + respondentTargetData.organization?.divisionNameJpn.substring(0, 4)
            : respondentTargetData.organization?.divisionCode
        }_${
          respondentTargetData.organization?.departmentCode.startsWith('TEMPORARY')
            ? '仮-' + respondentTargetData.organization?.departmentNameJpn.substring(0, 4)
            : respondentTargetData.organization?.departmentCode
        }` + `_${fileBaseName}_${createTime}.${extent}`,
      );
    }
  };
  // 配列が等しいか判定する関数
  function arraysEqual(a: any[], b: any[]): boolean {
    // 参照が同じ場合
    if (a === b) return true;
    // 長さが異なる場合
    if (a.length !== b.length) return false;

    // すべての要素を比較
    return a.every((item, index) => {
      // ファイル名とオブジェクトキー名が一致するか返す
      return item.fileName === b[index].fileName && item.objectKeyName === b[index].objectKeyName;
    });
  }
  // ファイル名のセルレンダラー関数
  const fileNameCellRenderer = (params: any) => {
    return (
      // ダウンロードリンク要素
      <a
        // スタイル設定（青色、下線）
        style={{ color: 'blue', textDecoration: 'underline' }}
        // href 属性（ダミー）
        href="#"
        // クリックイベントハンドラー
        onClick={(event) => {
          // デフォルト動作を防止
          event.preventDefault();
          // ファイルダウンロード処理を呼び出し
          handleDownloadFile(params.data);
        }}>
        {/* セル値を表示 */}
        {params.value}
      </a>
    );
  };

  // AG-Grid の列定義配列
  const columnDefs = [
    // 選択列定義
    {
      // ヘッダー名（選択）
      headerName: t('button.selection'),
      // フィールド名
      field: 'selected',
      // セルレンダラー（チェックボックス）
      cellRenderer: 'checkboxRenderer',
      // 幅設定
      width: 40,
      // サイズ適合抑制
      suppressSizeToFit: true,
      // チェックボックス選択有効
      checkboxSelection: true,
      // ナビゲーション抑制
      suppressNavigable: true,
      // ヘッダーチェックボックス選択有効
      headerCheckboxSelection: true,
      // フィルタ済みヘッダー選択のみ
      headerCheckboxSelectionFilteredOnly: true,
    },
    // ファイル名列定義
    {
      // ヘッダー名（ファイル名）
      headerName: t('enqueteAnswerInput.modal.fileAttachment.label.fileName'),
      // フィールド名
      field: 'name',
      // 幅設定
      width: 225,
      // ソート可能
      sortable: true,
      // フィルタリング可能
      filter: true,
      // セルスタイル（青色、下線）
      cellStyle: { color: 'blue', textDecoration: 'underline' },
      // カスタムセルレンダラー使用
      cellRenderer: fileNameCellRenderer,
      // リサイズ可能
      resizable: true,
    },
    // サイズ列定義
    {
      // ヘッダー名（サイズ）
      headerName: t('enqueteAnswerInput.modal.fileAttachment.label.size'),
      // フィールド名
      field: 'size',
      // 幅設定
      width: 110,
      // ソート可能
      sortable: true,
      // フィルタリング可能
      filter: true,
      // 値フォーマッター（バイト変換）
      valueFormatter: (params: any) => bytesToSize(params.value, 2),
    },
  ];
  // JSX レンダリング開始
  return (
    // モーダルコンポーネント（開閉状態と閉じるハンドラー）
    <Modal open={open} onClose={cancel} disableScrollLock={true}>
      {/* // Box コンポーネント（絶対配置、中央揃え） */}
      <Box
        // div 要素としてレンダリング
        component={'div'}
        // スタイル設定（位置、サイズ、背景など）
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 440,
          bgcolor: 'background.paper',
          // borderRadius: 2,
          boxShadow: 24,
          p: 3,
        }}>
        {/* クローズボタン（IconButton） */}
        <IconButton
          // aria-label 属性
          aria-label="close"
          // クリックイベント（キャンセル）
          onClick={cancel}
          // スタイル設定（位置、色）
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: (theme) => theme.palette.grey[500],
          }}>
          {/* 閉じるアイコン */}
          <CloseIcon />
        </IconButton>
        {/* モーダルの内容（タイトル） */}
        <h2>{t('enqueteAnswerInput.modal.fileAttachment.label.fileUpload')}</h2>

        {/* エラーメッセージ表示（存在する場合） */}
        {error && <p style={{ color: 'red' }}>{error}</p>}

        {/* 空の div（スペーサー） */}
        <div></div>

        {/* ファイル選択ボタン（Button） */}
        <Button
          style={{ marginTop: 20 }}
          variant="contained"
          component="label"
          sx={{ width: 90 }}
          disabled={selectedFiles.length >= 20 || !canEdit || !isEnqueteAnswerOpen}>
          {/* ボタンラベル（選択） */}
          {t('button.selection')}
          {/* ファイル入力要素（隠し） */}
          <input
            // 入力タイプ（ファイル）
            type="file"
            // 非表示
            hidden
            // 複数選択可能
            multiple
            // 変更イベントハンドラー
            onChange={handleFileChange}
          />
        </Button>

        {/* 注意書きテキスト（Typography） */}
        <Typography variant="body2" color="text.secondary">
          {/* 翻訳された注意書きメッセージ */}
          {t('enqueteAnswerInput.modal.fileAttachment.label.attachedFileNote')}
        </Typography>

        {/* 拡張子注意書きテキスト（Typography） */}
        <Typography variant="body2" color="text.secondary">
          {/* 翻訳された拡張子メッセージ */}
          {`${t('enqueteAnswerInput.modal.fileAttachment.label.attachedFileExtensionsHead')}`}
        </Typography>

        {/* AG-Grid のファイルリストコンテナ */}
        <div className="ag-theme-alpine" style={{ height: 250, marginTop: 20 }}>
          {/* AG-Grid React コンポーネント */}
          <AgGridReact
            // 列定義
            columnDefs={columnDefs}
            // {/* 行データ（選択されたファイル）
            rowData={selectedFiles.map((file) => ({
              name: file.fileName,
              size: file.size,
              objectKeyName: file.objectKeyName,
              selected: false,
              createdAt: file.createdAt,
              s3Path: file.s3Path,
              file: file.file,
            }))}
            // ページネーション有効
            pagination={true}
            // 複数行選択可能
            rowSelection="multiple"
            // Grid 準備完了時のハンドラー
            onGridReady={(params) => (gridApiRef.current = params.api)}
            // ページネーションパネル抑制
            suppressPaginationPanel={true}
            // 選択変更時のハンドラー
            onSelectionChanged={onSelectionChanged}
          />
        </div>

        {/* ボタン配置用 Box（削除と登録） */}
        <Box
          //  スタイル設定（フレックス、空間調整）
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 0,
          }}>
          {/* 削除ボタン（StyledButton） */}
          <StyledButton sx={{ width: 90 }} onClick={handleDeleteSelectedRows} isDisabled={isDisabled}>
            {/* 翻訳された削除ラベル */}
            {t('button.delete')}
          </StyledButton>
          {/* 登録ボタン（StyledButton） */}
          <StyledButton sx={{ width: 90 }} onClick={handleConfirm} isDisabled={onUpdate()}>
            {/* 翻訳された登録ラベル */}
            {t('enqueteAnswerInput.modal.fileAttachment.button.register')}
          </StyledButton>
        </Box>
        {/* 確認ダイアログ（Dialog） */}
        <Dialog open={confirmOpen} onClose={handleCancelConfirm}>
          {/* 閉じるボタン（IconButton） */}
          <IconButton
            // aria-label 属性
            aria-label="close"
            // クリックイベント（キャンセル確認）
            onClick={handleCancelConfirm}
            // スタイル設定（位置、色）
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: (theme) => theme.palette.grey[500],
            }}>
            {/* 閉じるアイコン */}
            <CloseIcon />
          </IconButton>
          {/* <DialogTitle>確認</DialogTitle> */}
          {/* コメントアウトされたダイアログタイトル */}
          {/* ダイアログコンテンツ（確認メッセージ） */}
          <DialogContent sx={{ pt: 6, pb: 0 }}>
            {/* 確認メッセージテキスト（改行対応） */}
            <Typography sx={{ whiteSpace: 'pre-line' }}>{t('enqueteAnswerInput.modal.fileAttachment.message.saveConfirmMsg')}</Typography>
          </DialogContent>
          {/* ダイアログアクション（ボタン配置） */}
          <DialogActions>
            {/* いいえボタン（エラー色） */}
            <StyledButton variant="contained" onClick={handleNoconfirm} color="error">
              {/* 翻訳されたいいえラベル */}
              {t('button.no')}
            </StyledButton>
            {/* はいボタン（プライマリ色） */}
            <StyledButton variant="contained" onClick={handleConfirm} color="primary">
              {/* 翻訳されたはいラベル */}
              {t('button.yes')}
            </StyledButton>
          </DialogActions>
        </Dialog>
      </Box>
    </Modal>
  );
};

// コンポーネントをデフォルトエクスポート
export default SelectFileModal;
